import { Typography } from "@mui/material";
import { ONBOARDING_COLORS } from "../../features/organisations/onboarding/onboardingDesign.js";

/**
 * Monospace fonts of the common platforms, so the code never falls back to a
 * proportional or unusually wide face. All of them advance about 0.6em per
 * character.
 */
const CODE_FONT_FAMILY = [
  "ui-monospace",
  '"SF Mono"',
  "SFMono-Regular",
  "Menlo",
  "Consolas",
  '"Roboto Mono"',
  '"Liberation Mono"',
  '"DejaVu Sans Mono"',
  '"Courier New"',
  "monospace",
].join(", ");

const LETTER_SPACING_EM = 0.05;
/** Widest advance of the fonts above in bold, rounded up. */
const CHARACTER_ADVANCE_EM = 0.62;
/** Share of the container's width the code may take. */
const FILL = 0.9;

/**
 * Font size at which `length` characters fill at most 90% of the nearest
 * size container's inline size, capped at `maxFontSize`.
 */
export function fittedCodeFontSize(length: number, maxFontSize: string) {
  const widthEm =
    Math.max(1, length) * (CHARACTER_ADVANCE_EM + LETTER_SPACING_EM);
  const containerShare = Math.floor((FILL * 10_000) / widthEm) / 100;
  return `min(${containerShare}cqi, ${maxFontSize})`;
}

interface Props {
  /** The code as it is displayed, e.g. `K7P-4QX` or `WDJB-MJHT`. */
  code: string;
  /** Size of the code when its container has room to spare. */
  maxFontSize: string;
  "data-testid"?: string;
  "aria-live"?: "polite" | "off";
}

/**
 * A code in large monospace digits, sized to its container rather than the
 * viewport. The nearest ancestor must set `containerType: "inline-size"`.
 */
export const FittedCode = ({
  code,
  maxFontSize,
  "data-testid": testId,
  "aria-live": ariaLive,
}: Props) => (
  <Typography
    component="p"
    aria-live={ariaLive}
    data-testid={testId}
    sx={{
      fontFamily: CODE_FONT_FAMILY,
      fontWeight: 700,
      letterSpacing: `${LETTER_SPACING_EM}em`,
      fontSize: fittedCodeFontSize(code.length, maxFontSize),
      lineHeight: 1.1,
      color: ONBOARDING_COLORS.heading,
      whiteSpace: "nowrap",
    }}
  >
    {code}
  </Typography>
);
